import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button/Button';
import { sanitizeHtml } from '../../../utils/sanitize';
import type { GdprDocument } from '../../../services/gdprService';
import './styles.css';

/** Títulos de respaldo mientras no existan las claves en los ficheros de idioma. */
const DEFAULT_TITLES: Record<string, string> = {
  privacy: 'Política de Privacidad',
  ai_transparency: 'Información de Transparencia sobre IA',
};

export function documentTitle(t: (key: string, fallback: string) => string, name: string): string {
  return t(`consent.documents.${name}`, DEFAULT_TITLES[name] ?? name);
}

interface ConsentGateProps {
  documents: GdprDocument[];
  /** Todos los documentos ya se aceptaron en una versión anterior. */
  isUpdate?: boolean;
  submitting?: boolean;
  error?: string | null;
  onAccept: (documents: GdprDocument[]) => void;
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 6.2 4.8 8.5 9.5 3.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function ConsentGate({
  documents,
  isUpdate = false,
  submitting = false,
  error = null,
  onAccept,
}: ConsentGateProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [reading, setReading] = useState<GdprDocument | null>(null);

  const allAccepted = useMemo(
    () => documents.length > 0 && documents.every((doc) => accepted.has(doc.name)),
    [documents, accepted]
  );

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const toggle = (name: string) => {
    setAccepted((current) => {
      const next = new Set(current);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const acceptFromReader = (doc: GdprDocument) => {
    setAccepted((current) => new Set(current).add(doc.name));
    setReading(null);
  };

  return createPortal(
    <div className="cg-backdrop" role="dialog" aria-modal="true" aria-labelledby="cg-title">
      <div className={reading ? 'cg cg--wide' : 'cg'}>
        {reading ? (
          <div className="cg__reader">
            <div className="cg__reader-top">
              <button type="button" className="cg__back" onClick={() => setReading(null)}>
                <span aria-hidden="true">←</span>
                {t('consent.back', 'Volver')}
              </button>
              <h2 className="cg__reader-title">{documentTitle(t, reading.name)}</h2>
              <span className="cg__reader-meta">
                {t('consent.version', 'Versión')} {reading.version}
              </span>
            </div>

            <div
              className="cg__reader-body"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(reading.content) }}
            />

            <div className="cg__reader-foot">
              {accepted.has(reading.name) ? (
                <Button variant="secondary" onClick={() => setReading(null)}>
                  {t('consent.back', 'Volver')}
                </Button>
              ) : (
                <Button onClick={() => acceptFromReader(reading)}>
                  {t('consent.readAndAccept', 'He leído y acepto')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="cg__main">
              <h2 className="cg__title" id="cg-title">
                {isUpdate
                  ? t('consent.updateTitle', 'Hemos actualizado nuestra información legal')
                  : t('consent.title', 'Antes de empezar')}
              </h2>
              <p className="cg__subtitle">
                {isUpdate
                  ? t(
                      'consent.updateSubtitle',
                      'Se ha publicado una nueva versión de estos documentos. Revísalos y acéptalos para continuar.'
                    )
                  : t('consent.subtitle', 'Revisa estos documentos y confirma que los has leído para continuar.')}
              </p>

              <div className="cg__list">
                {documents.map((doc) => {
                  const inputId = `cg-check-${doc.name}`;
                  return (
                    <div className="cg__item" key={doc.name}>
                      <label className="cg__check" htmlFor={inputId}>
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={accepted.has(doc.name)}
                          onChange={() => toggle(doc.name)}
                          disabled={submitting}
                        />
                        <span className="cg__box">
                          <CheckIcon />
                        </span>
                      </label>

                      <div className="cg__item-text">
                        <label className="cg__item-name" htmlFor={inputId}>
                          {documentTitle(t, doc.name)}
                        </label>
                        <span className="cg__item-hint">
                          {t('consent.itemHint', 'He leído, comprendo y acepto este documento')}
                        </span>
                      </div>

                      <button type="button" className="cg__item-read" onClick={() => setReading(doc)}>
                        {t('consent.read', 'Leer')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cg__footer">
              {error && <p className="cg__error">{error}</p>}
              <Button onClick={() => onAccept(documents)} disabled={!allAccepted || submitting}>
                {submitting ? t('consent.saving', 'Guardando...') : t('consent.accept', 'Aceptar y continuar')}
              </Button>
              <p className="cg__note">
                {t('consent.note', 'Se registrarán la versión aceptada, la fecha y la hora.')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
